import type { PostEnriched, RoundHole, UUID } from '@/lib/db/types';

function syntheticHoleId(holeNum: number): UUID {
  return `00000000-0000-4000-8000-${String(holeNum).padStart(12, '0')}` as UUID;
}

/** Personalize a round post so history/scorecard reflect the viewing member's gross scores. */
export function personalizeRoundPostForMember(
  post: PostEnriched,
  memberId: string,
): PostEnriched {
  if (!post.round) return post;

  const group = post.metadata?.groupScores?.find((g) => g.id === memberId);
  if (!group?.scores?.length) {
    if (post.author_id !== memberId) return post;
    return post;
  }

  const roundHoles: RoundHole[] = group.scores.map((h) => {
    const existing = post.round!.round_holes?.find((rh) => rh.hole_num === h.hole_num);
    if (existing) {
      return { ...existing, strokes: h.strokes, net_strokes: null };
    }
    return {
      id: syntheticHoleId(h.hole_num),
      round_id: post.round!.id,
      hole_num: h.hole_num,
      strokes: h.strokes,
      putts: null,
      fairway_hit: null,
      green_in_reg: null,
      net_strokes: null,
      notes: null,
    };
  });
  const gross = group.scores.reduce((sum, h) => sum + h.strokes, 0);

  return {
    ...post,
    round: {
      ...post.round,
      gross_total: gross,
      round_holes: roundHoles,
    },
  };
}

export function mergeMemberRoundPosts(
  authored: PostEnriched[],
  participated: PostEnriched[],
  memberId: string,
): PostEnriched[] {
  const seen = new Set<string>();
  const out: PostEnriched[] = [];

  const push = (p: PostEnriched) => {
    if (seen.has(p.id)) return;
    seen.add(p.id);
    if (p.type !== 'round' || !p.round?.gross_total) return;
    out.push(personalizeRoundPostForMember(p, memberId));
  };

  authored.forEach(push);
  participated.forEach(push);

  return out.sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
}
